// v10/src/components/admin/FlowStats.js
// Panel de estadísticas Flow Mode para instructor/admin
// v1.1 — Fix: colapso al click en piloto ya seleccionado + layout sin espacio en blanco
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart2, Users, Clock, AlertTriangle, ToggleLeft, ToggleRight, ChevronRight, ChevronDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import api from '../../services/apiService';

const FlowStats = ({ darkMode }) => {
  const [pilots, setPilots]           = useState([]);
  const [selectedPilot, setSelectedPilot] = useState(null);
  const [stats, setStats]             = useState(null);
  const [loading, setLoading]         = useState(true);
  const [toggling, setToggling]       = useState(null);

  const bg     = darkMode ? '#1e293b' : 'white';
  const border = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const text   = darkMode ? '#f1f5f9' : '#0f172a';
  const muted  = darkMode ? '#64748b' : '#94a3b8';

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/api/users');
        const allUsers = res.data?.data || res.data || [];
        setPilots(allUsers.filter(u => u.role === 'pilot'));
      } catch (_) {}
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    if (!selectedPilot) { setStats(null); return; }
    const fetchStats = async () => {
      try {
        const res = await api.get(`/api/flow/stats/${selectedPilot.id}`);
        setStats(res.data?.data || null);
      } catch (_) { setStats(null); }
    };
    fetchStats();
  }, [selectedPilot]);

  // ✅ Fix: toggle selección — si ya está seleccionado, colapsa
  const handleSelectPilot = (pilot) => {
    if (selectedPilot?.id === pilot.id) {
      setSelectedPilot(null);
      setStats(null);
    } else {
      setSelectedPilot(pilot);
    }
  };

  const handleToggle = async (pilot) => {
    setToggling(pilot.id);
    const newEnabled = !pilot.flow_mode_enabled;
    try {
      await api.put(`/api/flow/unlock/${pilot.id}`, { enabled: newEnabled });
      setPilots(prev => prev.map(p =>
        p.id === pilot.id ? { ...p, flow_mode_enabled: newEnabled } : p
      ));
      if (selectedPilot?.id === pilot.id) {
        setSelectedPilot(prev => ({ ...prev, flow_mode_enabled: newEnabled }));
      }
    } catch (_) {}
    setToggling(null);
  };

  const chartData = stats?.evolution?.map(e => ({
    month:   new Date(e.month).toLocaleDateString('es-AR', { month: 'short' }),
    flows:   parseInt(e.flows) || 0,
    trampas: parseInt(e.traps_confirmed) || 0,
    avgMs:   Math.round(parseFloat(e.avg_ms) || 0),
  })) || [];

  if (loading) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'60px 0' }}>
        <motion.div animate={{ rotate:360 }} transition={{ duration:1.5, repeat:Infinity, ease:'linear' }}
          style={{ width:'36px', height:'36px', borderRadius:'50%', border:'3px solid transparent', borderTopColor:'#818cf8' }} />
      </div>
    );
  }

  return (
    // ✅ Fix layout: width 100% + maxWidth para que no quede espacio en blanco
    <div style={{ width:'100%', maxWidth:'100%' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'20px' }}>
        <BarChart2 style={{ width:'22px', height:'22px', color:'#818cf8' }} />
        <h2 style={{ margin:0, fontSize:'18px', fontWeight:800, color:text }}>Estadísticas Flow Mode</h2>
      </div>

      {/* ── Lista de pilotos ── */}
      <div style={{ marginBottom:'24px' }}>
        <p style={{ margin:'0 0 10px', fontSize:'12px', fontWeight:700, color:muted, textTransform:'uppercase', letterSpacing:'0.05em' }}>
          <Users style={{ width:'12px', height:'12px', display:'inline', verticalAlign:'middle', marginRight:'4px' }} />
          Pilotos ({pilots.length})
        </p>
        <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
          {pilots.map(pilot => {
            const isSelected = selectedPilot?.id === pilot.id;
            return (
              <motion.div key={pilot.id} whileTap={{ scale:0.98 }}
                onClick={() => handleSelectPilot(pilot)}
                style={{
                  padding:'12px 14px', borderRadius:'14px', cursor:'pointer',
                  background: isSelected ? 'rgba(129,140,248,0.08)' : bg,
                  border: `1.5px solid ${isSelected ? '#818cf8' : border}`,
                  display:'flex', alignItems:'center', gap:'12px',
                  transition:'all 0.15s',
                }}>
                <div style={{
                  width:'36px', height:'36px', borderRadius:'10px', flexShrink:0,
                  background: pilot.flow_mode_enabled ? 'rgba(129,140,248,0.15)' : 'rgba(100,116,139,0.1)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontWeight:800, fontSize:'14px', color: pilot.flow_mode_enabled ? '#818cf8' : muted,
                }}>
                  {pilot.name?.charAt(0).toUpperCase() || '?'}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ margin:0, fontSize:'14px', fontWeight:600, color:text }}>{pilot.name}</p>
                  <p style={{ margin:'2px 0 0', fontSize:'11px', color:muted }}>
                    {pilot.total_inspections_completed || 0} inspecciones · {pilot.flow_mode_enabled ? '🌀 Flow activo' : 'Flow inactivo'}
                  </p>
                </div>
                <motion.button
                  onClick={(e) => { e.stopPropagation(); handleToggle(pilot); }}
                  whileTap={{ scale:0.9 }}
                  disabled={toggling === pilot.id}
                  style={{ background:'transparent', border:'none', cursor:'pointer', padding:'4px', display:'flex' }}>
                  {pilot.flow_mode_enabled
                    ? <ToggleRight style={{ width:'28px', height:'28px', color:'#818cf8' }} />
                    : <ToggleLeft  style={{ width:'28px', height:'28px', color:muted }} />
                  }
                </motion.button>
                {/* ✅ Fix: ícono indica si está expandido o colapsado */}
                {isSelected
                  ? <ChevronDown  style={{ width:'16px', height:'16px', color:'#818cf8', transition:'transform 0.2s' }} />
                  : <ChevronRight style={{ width:'16px', height:'16px', color:muted, opacity:0.4 }} />
                }
              </motion.div>
            );
          })}
          {pilots.length === 0 && (
            <p style={{ padding:'20px', textAlign:'center', fontSize:'13px', color:muted }}>No hay pilotos registrados</p>
          )}
        </div>
      </div>

      {/* ── Stats del piloto seleccionado ── */}
      <AnimatePresence>
        {selectedPilot && stats && (
          <motion.div
            initial={{ opacity:0, y:16 }}
            animate={{ opacity:1, y:0 }}
            exit={{ opacity:0, y:-8 }}
            transition={{ type:'spring', stiffness:120, damping:18 }}>

            <h3 style={{ margin:'0 0 16px', fontSize:'16px', fontWeight:700, color:text }}>
              📊 {stats.pilot?.name || selectedPilot.name}
            </h3>

            {/* KPIs */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:'10px', marginBottom:'20px' }}>
              {[
                { label:'Flows completados', value: stats.total_flows,                                    icon: BarChart2,    color:'#818cf8' },
                { label:'Trampas (30d)',      value: stats.traps_last_30d?.length || 0,                   icon: AlertTriangle, color:'#f59e0b' },
                { label:'Tiempo promedio',    value: stats.response_times?.avg_response_ms
                    ? `${(parseFloat(stats.response_times.avg_response_ms) / 1000).toFixed(1)}s` : '—',  icon: Clock,        color:'#06b6d4' },
              ].map((kpi, i) => (
                <div key={i} style={{ padding:'14px', borderRadius:'14px', background:`${kpi.color}08`, border:`1px solid ${kpi.color}20`, textAlign:'center' }}>
                  <kpi.icon style={{ width:'18px', height:'18px', color:kpi.color, margin:'0 auto 6px', display:'block' }} />
                  <p style={{ margin:0, fontSize:'20px', fontWeight:800, color:text }}>{kpi.value}</p>
                  <p style={{ margin:'2px 0 0', fontSize:'10px', fontWeight:600, color:muted }}>{kpi.label}</p>
                </div>
              ))}
            </div>

            {/* Gráfico evolución */}
            {chartData.length > 0 && (
              <div style={{ padding:'16px', borderRadius:'16px', background:bg, border:`1px solid ${border}`, marginBottom:'20px' }}>
                <p style={{ margin:'0 0 12px', fontSize:'13px', fontWeight:700, color:text }}>Evolución mensual</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartData} barCategoryGap="20%">
                    <CartesianGrid strokeDasharray="3 3" stroke={border} />
                    <XAxis dataKey="month" tick={{ fontSize:11, fill:muted }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize:11, fill:muted }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background:bg, border:`1px solid ${border}`, borderRadius:'10px', fontSize:'12px' }}
                      labelStyle={{ color:text, fontWeight:700 }} />
                    <Bar dataKey="flows"   fill="#818cf8" radius={[4,4,0,0]} name="Flows" />
                    <Bar dataKey="trampas" fill="#f59e0b" radius={[4,4,0,0]} name="Trampas" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Últimas trampas confirmadas */}
            {stats.traps_last_30d?.length > 0 && (
              <div style={{ padding:'16px', borderRadius:'16px', background:'rgba(245,158,11,0.04)', border:'1px solid rgba(245,158,11,0.12)' }}>
                <p style={{ margin:'0 0 10px', fontSize:'13px', fontWeight:700, color:'#fbbf24' }}>
                  ⚠ Trampas confirmadas (últimos 30 días)
                </p>
                {stats.traps_last_30d.map((trap, i) => (
                  <div key={i} style={{ padding:'8px 0', borderBottom: i < stats.traps_last_30d.length - 1 ? `1px solid rgba(245,158,11,0.08)` : 'none', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontSize:'13px', color:text }}>{trap.item_label}</span>
                    <span style={{ fontSize:'11px', color:muted }}>
                      {new Date(trap.created_at).toLocaleDateString('es-AR', { day:'2-digit', month:'short' })}
                      {trap.response_time_ms && ` · ${(trap.response_time_ms / 1000).toFixed(1)}s`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FlowStats;
